import {createApi, retry} from '@reduxjs/toolkit/query/react'
import {Except} from 'type-fest'

import {BookingStatus} from '../../../api-types/bovio/response/booking'
import {
  BookingStatus as BookingStatusResponse,
  BookingStatusLink,
  Status as BookingStatusApi
} from '../../../api-types/bovio/response/booking_status'
import {Bookings} from '../../../api-types/bovio/response/bookings'
import {
  fetchBaseQuery,
  FetchBaseQueryFnWithRetry,
  Method
} from '../../../utils/network/fetchBaseQuery'
import {BookingCreationResponse} from '../../types/BookingCreationResponse'
import {BookingFinalizationResponse} from '../../types/BookingFinalizationResponse'
import {BookingRequestWithPsd2} from '../../types/BookingRequest'
import {BookingsFiltersValues} from '../../types/BookingsFilterValues'
import {BookingsRequestsParams} from '../../types/BookingsRequestsParams'
import {InvoicePayload, InvoiceResponse} from '../../types/Invoice'
import {LinkType} from '../../types/Link'
import {SharedRequestParams} from '../../types/Request'
import {prepareBoVioHeaders} from '../../utils/prepareBovioHeaders'

type Link = LinkType | BookingStatusLink

export interface BookingCreationRequest extends SharedRequestParams {
  httpRequestId: string
  bookingRequestId: string
  vclid: string | undefined
  authToken: string | undefined
  link: Link
  payload: BookingRequestWithPsd2
}

export interface BookingFinalizationRequest extends SharedRequestParams {
  httpRequestId: string
  bookingRequestId: string
  link: Link
}

export interface PostEmailConfirmationRequest extends SharedRequestParams {
  link: Link
  payload: {
    email: string
  }
}

export interface GetBookingStatusRequest extends SharedRequestParams {
  httpRequestId: string
  bookingRequestId: string
  link: Link
}

export interface GetBookingsRequest extends SharedRequestParams {
  authToken: string
  link: string
  params: BookingsRequestsParams
  filter: BookingsFiltersValues
}

interface GetInvoiceRequest extends SharedRequestParams {
  url: string
}

export interface CreateInvoiceRequest extends SharedRequestParams {
  link: Link
  payload: InvoicePayload
}

export interface CancelBookingRequest extends SharedRequestParams {
  link: Link
  authToken?: string
  otpToken?: string
}

export interface GetSupportTokenRequest extends SharedRequestParams {
  url: string
  bookingId: string
  authToken?: string
  otpToken?: string
}

export interface SupportTokenResponse {
  supportToken: string
  expiresIn: number
}

const baseQueryWithRetry: FetchBaseQueryFnWithRetry = retry(
  fetchBaseQuery({
    baseUrl: ''
  }),
  {maxRetries: 0}
)

/**
 * API for managing booking-related operations.
 */
export const bookApi = createApi({
  reducerPath: 'bookApi',
  tagTypes: ['Bookings'],
  baseQuery: baseQueryWithRetry,
  serializeQueryArgs: ({endpointName}) => endpointName,
  endpoints: builder => ({
    createBooking: builder.mutation<
      BookingCreationResponse,
      BookingCreationRequest
    >({
      query: ({link, payload, ...rest}) => ({
        url: link.href,
        method: link.method as Method,
        data: Object.keys(payload).length !== 0 ? payload : undefined,
        headers: prepareBoVioHeaders(rest)
      })
    }),
    bookingFinalization: builder.mutation<
      BookingFinalizationResponse,
      BookingFinalizationRequest
    >({
      query: ({link, ...rest}) => ({
        url: link.href,
        method: link.method as Method,
        headers: prepareBoVioHeaders(rest)
      })
    }),
    getBookingStatus: builder.query<
      BookingStatusResponse,
      GetBookingStatusRequest
    >({
      query: ({link, ...rest}) => ({
        url: link.href,
        method: link.method as Method,
        headers: prepareBoVioHeaders(rest)
      }),
      extraOptions: {maxRetries: 3}
    }),
    sendBookingConfirmation: builder.mutation<
      void,
      PostEmailConfirmationRequest
    >({
      query: ({link, payload, ...rest}) => {
        return {
          url: link.href,
          method: link.method as Method,
          data: Object.keys(payload).length !== 0 ? payload : undefined,
          headers: prepareBoVioHeaders(rest)
        }
      }
    }),
    getBookings: builder.query<Bookings[], GetBookingsRequest>({
      query: ({link, params, ...rest}) => {
        return {
          url: link,
          headers: prepareBoVioHeaders(rest),
          params: params as Record<string, unknown>
        }
      },
      serializeQueryArgs: ({endpointName, queryArgs}) => {
        if (queryArgs) {
          const {filter} = queryArgs
          return {filter}
        }
        return endpointName
      },
      providesTags: () => ['Bookings']
    }),
    getInvoice: builder.query<InvoiceResponse, GetInvoiceRequest>({
      query: ({url, ...rest}) => ({
        url,
        headers: prepareBoVioHeaders(rest)
      })
    }),
    createInvoice: builder.mutation<InvoiceResponse, CreateInvoiceRequest>({
      query: ({link, payload, ...rest}) => ({
        url: link.href,
        method: link.method as Method,
        data: payload,
        headers: prepareBoVioHeaders(rest)
      })
    }),
    getSupportToken: builder.mutation<
      SupportTokenResponse,
      GetSupportTokenRequest
    >({
      query: ({url, bookingId, ...rest}) => ({
        url: `${url}secure/bookings/${bookingId}/support-token`,
        method: 'POST' as Method,
        headers: prepareBoVioHeaders(rest)
      })
    }),
    cancelBooking: builder.mutation<null, CancelBookingRequest>({
      queryFn: async ({link, ...rest}, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: link.href,
          method: link.method as Method,
          headers: prepareBoVioHeaders(rest)
        })

        return result.error ? {error: result.error} : {data: null}
      }
    })
  })
})

// We have a custom query key but there is no type support for that.
// So we cast that key type to request params type.
export const GET_BOOKING_STATUS_CACHE_KEY =
  'bookingStatus' as unknown as GetBookingStatusRequest
export const CREATE_BOOKING_CACHE_KEY = 'create-booking'
export const FINALIZE_BOOKING_CACHE_KEY = 'finalize-booking'

/**
 * Exported hooks for interacting with the bookApi
 */
export const {
  useCreateBookingMutation,
  useBookingFinalizationMutation,
  useGetBookingStatusQuery,
  useSendBookingConfirmationMutation,
  useGetBookingsQuery,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useCancelBookingMutation,
  useGetSupportTokenMutation
} = bookApi

type GetBookingStatusData = Except<
  ReturnType<typeof useGetBookingStatusQuery>,
  'refetch'
>

/**
 * Checks if the booking is initializing.
 * @param bookingStatusData - The data containing the booking status.
 * @returns True if the booking is initializing, otherwise false.
 */
export const getIsAsyncBookingInitializing = (
  bookingStatusData: GetBookingStatusData
) =>
  bookingStatusData.isLoading ||
  (bookingStatusData.isSuccess &&
    bookingStatusData.data?.status === BookingStatusApi.Initialized)

/**
 * Checks if the booking is finalizing.
 * @param bookingStatusData - The data containing the booking status.
 * @returns True if the booking is finalizing, otherwise false.
 */
export const getIsAsyncBookingFinalizing = (
  bookingStatusData: GetBookingStatusData
) =>
  bookingStatusData.isLoading ||
  (bookingStatusData.isSuccess &&
    bookingStatusData.data?.status === BookingStatusApi.Finalizing)

/**
 * Checks if the booking is either initializing or finalizing.
 * @param bookingStatusData - The data containing the booking status.
 * @returns True if the booking is initializing or finalizing, otherwise false.
 */
export const getIsAsyncBookingProcessing = (
  bookingStatusData: GetBookingStatusData
) =>
  getIsAsyncBookingInitializing(bookingStatusData) ||
  getIsAsyncBookingFinalizing(bookingStatusData)

/**
 * Checks if the booking status is confirmed.
 * @param status - The booking status to check.
 * @returns True if the status is confirmed, otherwise false.
 */
export const isStatusConfirmed = (status: BookingStatus | string) => {
  return (
    status === BookingStatus.Confirmed ||
    status === BookingStatus.ChargeLaterConfirmed ||
    status === BookingStatus.PaymentAuthenticateLaterConfirmed
  )
}

/**
 * Checks if the booking status is confirmed.
 * @param bookingStatusData - The data containing the booking status.
 * @returns True if the booking status is confirmed, otherwise false.
 */
export const getIsBookingStatusConfirmed = (
  bookingStatusData: GetBookingStatusData
) =>
  bookingStatusData.isSuccess &&
  isStatusConfirmed(bookingStatusData.data?.status)

/**
 * Checks if the booking status requires three-domain secure authentication.
 * @param bookingStatusData - The data containing the booking status.
 * @returns True if the booking status requires three-domain secure authentication, otherwise false.
 */
export const getIsBookingStatusThreeDomainSecureRequired = (
  bookingStatusData: GetBookingStatusData
) =>
  bookingStatusData.isSuccess &&
  bookingStatusData.data?.status === BookingStatus.ThreeDomainSecureRequired

/**
 * Checks if the booking status indicates an error.
 * @param bookingStatusData - The data containing the booking status.
 * @returns True if the booking status is an error, otherwise false.
 */
export const getIsBookingStatusError = (
  bookingStatusData: GetBookingStatusData
) =>
  bookingStatusData.isError ||
  (bookingStatusData.isSuccess &&
    bookingStatusData.data?.status === BookingStatus.Error)
